/**
 * Web counterpart of gamePack.ts. The web build serves the packs itself from public/
 * (scripts/copy-frontend-assets.js), so a pack is just a folder on this origin and
 * @dr.pogodin/react-native-fs, which has no web implementation and throws at import
 * time, is never loaded. Same exports as gamePack.ts; keep the two in step.
 */
export type GamePackSource = 'bundled' | 'dev-server';

export interface GamePack {
	source: GamePackSource;
	/** Directory URL of the pack, without a trailing slash. */
	baseUrl: string;
	/** The entry document the host loads (the Svara table pack has a single entry). */
	entryUrl: string;
	/** iOS only; always undefined on web. */
	readAccessUrl?: string;
}

export interface GamePackManifest {
	name: string;
	version: string;
	entry?: string;
}

export const GAME_PACK_DIR = 'svaragame';
export const GAME_PACK_ENTRY = 'embed.html';
export const MINI_GAMES_PACK_DIR = 'miniGames';

// Served over HTTP like a dev-server pack, so readGamePackManifest fetches it.
const packLocation = (dirName: string): Pick<GamePack, 'source' | 'baseUrl'> => ({
	source: 'dev-server',
	baseUrl: `${window.location.origin}/${dirName}`,
});

/** The Svara table game pack. */
export function resolveGamePack(): GamePack {
	const location = packLocation(GAME_PACK_DIR);
	return { ...location, entryUrl: `${location.baseUrl}/${GAME_PACK_ENTRY}` };
}

/** The mini-games pack (scratch, black-and-red, slot). */
export function resolveMiniGamesPack(): Omit<GamePack, 'entryUrl'> {
	return packLocation(MINI_GAMES_PACK_DIR);
}

/** True when `url` addresses something inside the pack (entry, scripts, assets). */
export const isGamePackUrl = (pack: GamePack, url: string) =>
	url === pack.baseUrl || url.startsWith(`${pack.baseUrl}/`);

/** Reads the pack's manifest. Best effort: null when it is missing or unreadable. */
export async function readGamePackManifest(pack: GamePack): Promise<GamePackManifest | null> {
	try {
		const res = await fetch(`${pack.baseUrl}/manifest.json`);
		if (!res.ok) return null;
		const parsed = await res.json();
		return parsed && typeof parsed.version === 'string' ? (parsed as GamePackManifest) : null;
	} catch {
		return null;
	}
}
